/**
 * Custom hook to manage markers on floor images
 */

import { useEffect, useRef } from '@wordpress/element';
import markerManager from './MarkerManager';

const useMarkerEffect = (isLoading, selectedFloors, floorData) => {
    const updateMarkerTimeout = useRef(null);

    // Clean up all markers and timeouts when component unmounts
    useEffect(() => {
        return () => {
            markerManager.removeAllMarkers();
            if (updateMarkerTimeout.current) clearTimeout(updateMarkerTimeout.current);
        };
    }, []);

    // Add markers once floor data is loaded and rendered
    useEffect(() => {
        // Wait for DOM to be ready after render
        if (!isLoading && selectedFloors && floorData) {
            // Clear any existing timeout
            if (updateMarkerTimeout.current) {
                clearTimeout(updateMarkerTimeout.current);
            }

            // Give time for the floor images to render before adding markers
            updateMarkerTimeout.current = setTimeout(() => {
                // First remove all existing markers
                markerManager.removeAllMarkers();

                // Then add markers for each floor
                selectedFloors.forEach((floor) => {
                    const floorId = floor.floorId;
                    const floorPost = floorData[floorId];

                    // Check if floor post has markers
                    if (floorPost && floorPost.markers && Array.isArray(floorPost.markers)) {
                        floorPost.markers.forEach((marker, index) => {
                            // Skip markers without valid coordinates
                            if (!marker.focalPointX || !marker.focalPointY) {
                                return;
                            }

                            // Create unique marker ID
                            const markerId = `${floorId}-marker-${index}`;

                            // Create marker title from categories if available
                            let markerTitle = '';
                            if (marker.categories && Array.isArray(marker.categories)) {
                                markerTitle = marker.categories.join(', ');
                            }

                            // Place the marker on the floor image
                            markerManager.createOrUpdateMarker(
                                floorId,
                                markerId,
                                marker.focalPointX,
                                marker.focalPointY,
                                markerTitle
                            );
                        });
                    }
                });
            }, 500); // 500ms delay to ensure DOM is ready
        }

        return () => {
            if (updateMarkerTimeout.current) {
                clearTimeout(updateMarkerTimeout.current);
            }
        };
    }, [isLoading, selectedFloors, floorData]);
};

export default useMarkerEffect;
